import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Loader2, FileText, ExternalLink, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const field = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy outline-none focus:border-navy focus:ring-2 focus:ring-navy/20";
const label = "mb-1 block text-xs font-semibold text-navy";

const empty = {
  title: "", slug: "", parent: null, body: "", status: "published",
  show_in_footer: false, show_in_nav: false, order: 0, seo_description: "",
};

export const PagesManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pages");
      setPages(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...empty, ...p }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0, parent: form.parent || null };
      if (editing) {
        await api.put(`/admin/pages/${editing}`, payload);
        toast.success("Page updated");
      } else {
        await api.post("/admin/pages", payload);
        toast.success("Page created");
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this page and all its subpages?")) return;
    try { await api.delete(`/admin/pages/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const parentTitle = (pid) => pages.find((p) => p.id === pid)?.title;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  return (
    <div data-testid="pages-manager">
      <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-5 py-4">
        <div>
          <p className="font-display text-lg font-black text-navy">Pages & Subpages</p>
          <p className="text-sm text-muted-foreground">Create and manage standalone pages (Privacy, Terms, custom pages…).</p>
        </div>
        <button onClick={openNew} data-testid="new-page-btn" className="inline-flex items-center gap-2 rounded-lg bg-brandred px-5 py-2.5 text-sm font-bold text-white hover:bg-brandred-700">
          <Plus size={16} /> New Page
        </button>
      </div>

      <div className="grid gap-3">
        {pages.length === 0 ? (
          <p className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-muted-foreground">No pages yet. Create your first page.</p>
        ) : pages.map((p) => (
          <div key={p.id} data-testid={`page-row-${p.id}`} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-navy-50 text-navy"><FileText size={16} /></span>
              <div>
                <p className="font-display font-bold text-navy">{p.title}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <code className="rounded bg-gray-100 px-1.5 py-0.5">/{p.path}</code>
                  {p.parent && <span>↳ under {parentTitle(p.parent) || "—"}</span>}
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${p.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{p.status}</span>
                  {p.show_in_footer && <span className="rounded-full bg-navy-50 px-2 py-0.5">footer</span>}
                  {p.show_in_nav && <span className="rounded-full bg-gold/20 px-2 py-0.5 text-gold-700">nav</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a href={`/${p.path}`} target="_blank" rel="noreferrer" className="rounded-lg border border-gray-200 p-2 text-navy hover:bg-navy-50" title="View"><ExternalLink size={16} /></a>
              <button onClick={() => openEdit(p)} data-testid={`edit-page-${p.id}`} className="rounded-lg border border-gray-200 p-2 text-navy hover:bg-navy-50"><Pencil size={16} /></button>
              <button onClick={() => remove(p.id)} data-testid={`delete-page-${p.id}`} className="rounded-lg border border-red-200 p-2 text-brandred hover:bg-red-50"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10">
          <form onSubmit={save} data-testid="page-form" className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-black text-navy">{editing ? "Edit" : "New"} Page</h2>
              <button type="button" onClick={() => setShowForm(false)}><X className="text-gray-400" /></button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label}>Title *</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} data-testid="page-title" />
              </div>
              <div>
                <label className={label}>URL slug (auto if blank)</label>
                <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={field} placeholder="e.g. about-us" data-testid="page-slug" />
              </div>
              <div>
                <label className={label}>Parent page (for subpages)</label>
                <select value={form.parent || ""} onChange={(e) => setForm({ ...form, parent: e.target.value || null })} className={field} data-testid="page-parent">
                  <option value="">— None (top level) —</option>
                  {pages.filter((p) => p.id !== editing).map((p) => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={field} data-testid="page-status">
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <div>
                <label className={label}>Menu order</label>
                <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={field} />
              </div>
              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                  <input type="checkbox" checked={form.show_in_footer} onChange={(e) => setForm({ ...form, show_in_footer: e.target.checked })} data-testid="page-footer" /> Footer
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                  <input type="checkbox" checked={form.show_in_nav} onChange={(e) => setForm({ ...form, show_in_nav: e.target.checked })} data-testid="page-nav" /> Nav
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className={label}>SEO description</label>
                <input value={form.seo_description || ""} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className={field} />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Content (Markdown supported: ## headings, **bold**, - lists, [links](url))</label>
                <textarea rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${field} font-mono text-xs`} data-testid="page-body" />
              </div>
            </div>

            <button type="submit" disabled={saving} data-testid="save-page-btn" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brandred px-6 py-3 text-sm font-bold text-white hover:bg-brandred-700 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? "Save Changes" : "Create Page"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
